import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { stringSorter } from "../../utils/functions.js";
import DataTable from "../../components/CustomDatatable/index.js";
import {
    Button,
    Card,
    Space,
    Typography,
    Breadcrumb,
    Row,
    Col,
    Popconfirm,
} from "antd";
import {
    EditOutlined,
    HomeOutlined,
    PlusOutlined,
    DeleteOutlined,
} from "@ant-design/icons";
import { Spinner } from "reactstrap";
import ColorsForm from "./ColorForm.js";
import { enhanceColumnsWithFilters } from "../../components/ColumnFilters/index.js";
import {
    addColor,
    deleteColor,
    getColors,
    updateColor,
    updateColorStatus,
} from "../../store/actions/color.js";
import moment from "moment-timezone";
import CustomToggleSwitch from "../../components/CustomToggleSwitch.js";

const { Title } = Typography;

const Colors = () => {
    document.title = "Colors";
    const [isLoading, setIsLoading] = useState(true);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRecord, setSelectedRecord] = useState(null);
    const [deletingIds, setDeletingIds] = useState([]);

    const dispatch = useDispatch();
    const colorsFromStore = useSelector((state) => state?.color?.list ?? []);

    useEffect(() => {
        dispatch(getColors()).finally(() => setIsLoading(false));
    }, [dispatch]);

    // ─── ADD / UPDATE ─────────────────────────────────────────────────────────
    const handleCreate = async (values) => {
        if (selectedRecord) {
            const changedFields = {};
            if (values.name !== selectedRecord.name) changedFields.name = values.name;
            if (values.code !== selectedRecord.code) changedFields.code = values.code;
            if ((values.remarks ?? "") !== (selectedRecord.remarks ?? "")) changedFields.remarks = values.remarks ?? "";

            if (Object.keys(changedFields).length > 0) {
                await dispatch(updateColor({ id: Number(selectedRecord.id), ...changedFields }));
                await dispatch(getColors());
            }
        } else {
            const payload = {
                name: values.name,
                code: values.code,
                isForAll: values.isForAll ?? false,
                ...(values.remarks ? { remarks: values.remarks } : {}),
            };
            await dispatch(addColor(payload));
            await dispatch(getColors());
        }

        setIsModalVisible(false);
        setSelectedRecord(null);
    };

    // ─── DELETE — NO getColors() after delete ─────────────────────────────────
    const handleDelete = async (id) => {
        setDeletingIds((prev) => [...prev, id]);
        try {
            await dispatch(deleteColor(id));
        } catch (error) {
            console.error("Failed to delete color:", error);
        } finally {
            setDeletingIds((prev) => prev.filter((d) => d !== id));
        }
    };

    // ─── STATUS ───────────────────────────────────────────────────────────────
    const handleChangeStatus = async (id, status) => {
        await dispatch(updateColorStatus(id, status));
        await dispatch(getColors());
    };

    const handleEdit = (record) => { setSelectedRecord(record); setIsModalVisible(true); };
    const handleCancel = () => { setIsModalVisible(false); setSelectedRecord(null); };

    const renderCell = (text) => (text ? text : "N/A");

    let columns = [
        {
            title: "Name",
            dataIndex: "name",
            key: "name",
            sorter: stringSorter("name"),
            sortDirections: ["ascend", "descend"],
        },
        {
            title: "Code",
            dataIndex: "code",
            key: "code",
        },
        {
            title: "Remarks",
            dataIndex: "remarks",
            key: "remarks",
            render: renderCell,
        },
        {
            title: "Created At",
            dataIndex: "createdAt",
            key: "createdAt",
            render: (date) => moment(date).isValid() ? moment(date).format("YYYY-MM-DD HH:mm:ss") : "",
            sorter: (a, b) =>
                moment(a.createdAt).isValid() && moment(b.createdAt).isValid()
                    ? moment(a.createdAt).unix() - moment(b.createdAt).unix() : 0,
            sortDirections: ["ascend", "descend"],
        },
        {
            title: "Status",
            dataIndex: "status",
            key: "status",
            render: (status, record) => (
                <CustomToggleSwitch
                    record={record}
                    onChangeStatus={handleChangeStatus}
                />
            ),
            sorter: (a, b) => a.status.localeCompare(b.status),
        },
        {
            title: "Action",
            key: "action",
            render: (_, record) => (
                <Space size="small">
                    <Button
                        type="link"
                        icon={<EditOutlined />}
                        onClick={() => handleEdit(record)}
                        title="Edit"
                    />
                    <Popconfirm
                        title="Are you sure you want to delete this record?"
                        onConfirm={() => handleDelete(record.id)}
                        okText="Yes"
                        cancelText="No"
                    >
                        <Button
                            type="link"
                            danger
                            icon={<DeleteOutlined />}
                            title="Delete"
                            loading={deletingIds.includes(record.id)}
                        />
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    columns = enhanceColumnsWithFilters(columns);

    return (
        <div className="page-content">
            <Breadcrumb style={{ marginBottom: "16px" }}>
                <Breadcrumb.Item href="/"><HomeOutlined /></Breadcrumb.Item>
                <Breadcrumb.Item href="/dashboard">Dashboard</Breadcrumb.Item>
                <Breadcrumb.Item>Colors</Breadcrumb.Item>
            </Breadcrumb>

            <Card>
                <Space direction="vertical" size="middle" style={{ width: "100%" }}>
                    <Row justify="space-between" align="middle">
                        <Col>
                            <Title level={2} style={{ margin: 0 }}>Manage Colors</Title>
                        </Col>
                        <Col>
                            <Button
                                type="primary"
                                icon={<PlusOutlined />}
                                onClick={() => { setSelectedRecord(null); setIsModalVisible(true); }}
                            >
                                Add Color
                            </Button>
                        </Col>
                    </Row>

                    {isLoading ? (
                        <div className="d-flex justify-content-center">
                            <Spinner animation="border" variant="primary" />
                        </div>
                    ) : (
                        <DataTable
                            dataSource={colorsFromStore.map((item) => ({ ...item, key: item.id }))}
                            columns={columns}
                        />
                    )}
                </Space>
            </Card>

            <ColorsForm
                visible={isModalVisible}
                onCreate={handleCreate}
                onCancel={handleCancel}
                record={selectedRecord}
            />
        </div>
    );
};

export default Colors;